import { useState, useEffect, useRef } from "react";
import { useLocation, Link } from "react-router-dom";
import { getPromoPrefix } from "../utils/promoPath";
import logo from "../images/explogo.png";
import logo2 from "../images/explogo@2x.png";
// import shield2x from "../images/shield@2x.png";
// import bulletGreen from "../images/bullet1_green.png";
import mcafee from "../images/McAffee.png";
import mcafee2 from "../images/McAffee@2x.png";
import norton from "../images/Norton.png";
import norton2 from "../images/Norton@2x.png";
import rated from "../images/Rated.png";
import rated2 from "../images/Rated@2x.png";

const STEPS = [
  { label: "QUALIFY", path: "/eligibility" },
  { label: "APPLY", path: "/qualify" },
  { label: "SUBMIT", path: "/important" },
];
const SCROLL_THRESHOLD_DOWN = 150; // shrink when scrolling DOWN past this
const SCROLL_THRESHOLD_UP = 80; // un-shrink only when scrolled back UP past this
// ✅ Dead zone between 80–150px prevents jerk loop caused by spacer height change

const isDesktop = () =>
  typeof window !== "undefined" && window.innerWidth >= 1024;

const TRANSITION = "0.35s ease";

// ✅ Fallback values only — real height is measured live via ResizeObserver.
// Kept as sane initial paint values before the ref measurement kicks in.
const HEADER_HEIGHT_FULL = 158;
const HEADER_HEIGHT_SMALL = 100;

const Header = () => {
  const { pathname: rawPathname, search } = useLocation();
  const promoPrefix = getPromoPrefix(rawPathname);
  const pathname = promoPrefix ? rawPathname.slice(promoPrefix.length) || "/" : rawPathname;
  const [scrolled, setScrolled] = useState(false);
  const [desktop, setDesktop] = useState(isDesktop());
  const scrolledRef = useRef(false);
  const headerRef = useRef(null);
  const [headerHeight, setHeaderHeight] = useState(HEADER_HEIGHT_FULL);

  useEffect(() => {
    const onResize = () => setDesktop(isDesktop());
    window.addEventListener("resize", onResize, { passive: true });
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    setScrolled(false);
    scrolledRef.current = false;

    if (!desktop) return;

    const syncScrollState = () => {
      const currentlyScrolled = window.scrollY > SCROLL_THRESHOLD_DOWN;
      scrolledRef.current = currentlyScrolled;
      setScrolled(currentlyScrolled);
    };

    syncScrollState();

    // ✅ Safari fires scroll events out of step with paint; reading/writing
    // on the raw event (rather than aligned to a paint frame) is what caused
    // the header to visibly jerk there. Throttling to one check per frame
    // keeps the state flip in sync with Safari's own compositing.
    let rafId = null;
    const onScroll = () => {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        const y = window.scrollY;
        if (!scrolledRef.current && y > SCROLL_THRESHOLD_DOWN) {
          // ✅ Shrink — only when scrolled well past 150px
          scrolledRef.current = true;
          setScrolled(true);
        } else if (scrolledRef.current && y < SCROLL_THRESHOLD_UP) {
          // ✅ Un-shrink — only when scrolled back above 80px
          scrolledRef.current = false;
          setScrolled(false);
        }
        // ✅ Between 80–150px: do nothing — dead zone kills the jerk loop
      });
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, [pathname, desktop]);

  const getActiveIndex = () => {
    const params = new URLSearchParams(search);
    const stepOverride = params.get("step");

    if (stepOverride !== null) {
      return parseInt(stepOverride, 10);
    }

    // Landing page - no active step
    if (pathname === "/") {
      return -1;
    }

    // QUALIFY
    if (pathname === "/eligibility" || pathname.startsWith("/eligibility/")) {
      return 0;
    }

    // APPLY
    if (
      pathname === "/qualify" ||
      pathname === "/shipping" ||
      pathname === "/checkout" ||
      pathname === "/confirmation" ||
      pathname.startsWith("/qualify/") ||
      pathname.startsWith("/shipping/") ||
      pathname.startsWith("/checkout/") ||
      pathname.startsWith("/confirmation/")
    ) {
      return 1;
    }

    // SUBMIT
    if (pathname === "/important" || pathname.startsWith("/important/")) {
      return 2;
    }

    return -1;
  };

  const activeIndex = getActiveIndex();
  const animate = desktop && scrolled;
  const isLanding = pathname === "/";

  // ✅ Measure the header's real rendered height whenever its size changes
  // (content, breakpoint, animate state, route, etc.) instead of guessing
  // with fixed pixel constants. This keeps the spacer div always accurate,
  // so page content never overlaps the fixed header.
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;

    const measure = () => setHeaderHeight(el.offsetHeight);
    measure();

    if (typeof ResizeObserver !== "undefined") {
      const ro = new ResizeObserver(measure);
      ro.observe(el);
      return () => ro.disconnect();
    } else {
      // Fallback for environments without ResizeObserver support
      window.addEventListener("resize", measure, { passive: true });
      return () => window.removeEventListener("resize", measure);
    }
  }, [animate, isLanding, desktop, pathname]);

  return (
    <>
      {/* ✅ Spacer prevents content from hiding behind fixed header */}
      <div
        className="mobHeader"
        style={{
          height: `${headerHeight}px`,
          flexShrink: 0,
        }}
      />
      <header
        ref={headerRef}
        className="w-full "
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 50,
          transform: "translateZ(0)",
          WebkitBackfaceVisibility: "hidden",
          // ✅ A blurred box-shadow forces Safari to repaint (not just
          // composite) a position:fixed element on every scroll frame,
          // which is visible as a jerk while scrolling in the shrunk state.
          // A solid, unblurred border has no blur to recompute per frame.
          borderBottom: animate
            ? "1px solid rgba(0,0,0,0.18)"
            : "1px solid transparent",
          transition: `border-bottom-color ${TRANSITION}`,
        }}
      >
        {/* ── TOP INFO BAR ────────────────────────────────────────────────── */}
        <div className="# mBHDR border-b border-[#0f2256]" style={{background:"#061557"}}>
          <div
            className="max-w-7xl mx-auto px-4 lg:px-6 flex items-center justify-center lg:justify-between gap-4 relative"
            style={{
              paddingTop: animate ? "5px" : "8px",
              paddingBottom: animate ? "5px" : "8px",
              transition: `padding ${TRANSITION}`,
            }}
          >
            {/* LEFT — logo */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <Link to={promoPrefix || "/"} className="flex flex-shrink-0 items-center">
                <div
                  className="flex-shrink-0 flex items-center select-none"
                  style={{
                    height: animate ? "40px" : "50px",
                    padding: "3px 8px",
                    background: "#ffffff",
                    borderRadius: "8px",
                    transition: `height ${TRANSITION}`,
                    willChange: "height",
                  }}
                >
                  <img
                    src={logo}
                    srcSet={`${logo} 1x, ${logo2} 2x`}
                    alt="Expedite Concealed Carry"
                    className="logo"
                    loading="eager"
                    decoding="async"
                    style={{
                      height: "100%",
                      width: "auto",
                      display: "block",
                    }}
                  />
                </div>
              </Link>
            </div>

            {/* CENTER — tagline */}
            <div className="hidden lg:block flex-1 text-center px-4 select-none">
              <p
                style={{
                  fontFamily: "'Acumin Pro Wide', serif",
                  color: "rgba(255,255,255,0.85)",
                  fontSize: animate ? "9px" : "11px",
                  lineHeight: 1.35,
                  margin: 0,
                  textAlign:"left",
                  transition: `font-size ${TRANSITION}`,
                }}
              >
               Concealed Carry Application Support Specialists.<br></br>
End-To-End Guidance for Pre-Qualification<br></br>
and Compliant Application Submissions.
              </p>
            </div>

            {/* RIGHT — trust badges */}
            <div className="hidden lg:flex items-center gap-3 flex-shrink-0 select-none">
              <img
                src={mcafee}
                srcSet={`${mcafee} 1x, ${mcafee2} 2x`}
                alt="McAfee Secure"
                loading="eager"
                decoding="async"
                style={{
                  height: animate ? "31px" : "37px",
                  width: "auto",
                  transition: `height ${TRANSITION}`,
                }}
              />
              <img
                src={norton}
                srcSet={`${norton} 1x, ${norton2} 2x`}
                alt="Norton Secured"
                loading="eager"
                decoding="async"
                style={{
                  height: animate ? "36px" : "42px",
                  width: "auto",
                  transition: `height ${TRANSITION}`,
                }}
              />
              <div className="flex flex-col items-center">
                <img
                  src={rated}
                  srcSet={`${rated} 1x, ${rated2} 2x`}
                  alt="5 star rating"
                  loading="eager"
                  decoding="async"
                  style={{
                    height: animate ? "31px" : "37px",
                    width: "auto",
                    transition: `height ${TRANSITION}`,
                  }}
                />

              </div>
            </div>
          </div>
        </div>

        {/* ── STEPPER + CONTACT BAR ───────────────────────────────────────── */}
        <nav
          style={{
            width: "100%",
            background: "white",
            borderBottom: "1px solid #000",
          }}
        >
          <div
            className="max-w-7xl mx-auto px-4 lg:px-6 grid items-center"
            style={{
              gridTemplateColumns: "1fr auto 1fr",
              height: animate ? "28px" : "36px",
              overflow: "hidden",
              transition: `height ${TRANSITION}`,
            }}
          >
            <div />
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifySelf: "center",
                transformOrigin: "center center",
                transform: animate ? "scale(0.85)" : "scale(1)",
                transition: `transform ${TRANSITION}`,
                willChange: "transform",
              }}
            >
              {STEPS.map((step, i) => {
                const reached = activeIndex !== -1 && i <= activeIndex;
                const completed = activeIndex !== -1 && i < activeIndex;
                const current = i === activeIndex;
                return (
                  <div
                    key={step.label}
                    style={{ display: "flex", alignItems: "center" }}
                  >
                    {i > 0 && (
                      <div
                        className="line-top"
                        style={{
                          width: "50px",
                          height: "2px",
                          backgroundColor:
                            reached ? "#6DBE45" : "#d1d5db",
                          transition: "background-color 0.5s",
                          flexShrink: 0,
                        }}
                      />
                    )}
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          borderRadius: "50%",
                          width: "18px",
                          height: "18px",
                          flexShrink: 0,
                          fontSize: "11px",
                          fontWeight: 700,
                          fontFamily: "stratos, sans-serif",
                          transition: "background-color 0.5s, color 0.5s",
                          backgroundColor: completed
                            ? "#6DBE45"
                            : current
                            ? "#6DBE45"
                            : "#000000",
                          border: "none",
                          color: "#ffffff",
                        }}
                      >
                        {i + 1}
                      </div>
                      <span
                        style={{
                          fontSize: "13px",
                          fontWeight: "500",
                          letterSpacing: "0.08em",
                          color: completed
                            ? "#6DBE45"
                            : current
                            ? "#6DBE45"
                            : "#9ca3af",
                          fontFamily: "stratos, sans-serif",
                        }}
                      >
                        {step.label}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>

            <div
              className="hidden lg:flex items-center gap-2 flex-shrink-0"
              style={{ justifySelf: "end" }}
            >
              <svg
                width="16"
                height="12"
                viewBox="0 0 21.039 15.766"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M18.615 0H2.423A2.426 2.426 0 0 0 0 2.423v10.92a2.426 2.426 0 0 0 2.423 2.424h16.192a2.426 2.426 0 0 0 2.424-2.424V2.423A2.426 2.426 0 0 0 18.615 0m-5.748 7.883 7.214-6.219a1.638 1.638 0 0 1 .186.759v10.92a1.64 1.64 0 0 1-.187.76zM18.615.772a1.641 1.641 0 0 1 .959.309l-9.055 7.807-9.054-7.807a1.64 1.64 0 0 1 .958-.309zM.958 14.1a1.64 1.64 0 0 1-.186-.76V2.423a1.64 1.64 0 0 1 .186-.759l7.214 6.219zm1.465.892a1.641 1.641 0 0 1-.958-.309l7.3-6.292 1.5 1.3a.386.386 0 0 0 .5 0l1.5-1.3 7.3 6.292a1.642 1.642 0 0 1-.958.309z"
                  fill="#000000"
                />
              </svg>
              <Link
                to="mailto:support@expediteconcealedcarry.com"
                style={{
                  fontFamily: "sans-serif",
                  fontSize: "12px",
                  color: "#000000",
                  fontWeight: "400",
                }}
                className="hover:text-black transition-colors"
              >
                support@expediteconcealedcarry.com
              </Link>
            </div>
          </div>
        </nav>

        {/* ── MOBILE PORTAL BANNER ─────────────────────────────────────────── */}
        <div
          className="md:hidden w-full text-center"
          style={{ background: "#061557" }}
        >
          <p
            style={{
              margin: 0,
              padding: "6px 12px",
              color: "#ffffff",
              fontSize: "11px",
              fontWeight: 700,
              letterSpacing: "0.08em",
              fontFamily: "stratos, sans-serif",
            }}
          >
            PROCEED TO APPLICATION PORTAL
          </p>
        </div>
      </header>
    </>
  );
};

export default Header;
